import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { View, StyleSheet } from 'react-native'
import {
  Text,
  Avatar,
  List,
  Menu,
  Chip,
  IconButton,
  Button,
} from 'react-native-paper'
import PaginatedFlatList from '../../../../@ui/containers/lists/paginated-flat.list'
import {
  NodeByIdQuery,
  NodeMember,
  useNodeMembersByNodeIdQuery,
  useUpdateNodeMemberMutation,
} from '../../../../generated/graphql'
import { useTranslation } from 'react-i18next'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import { useTheme } from '../../../../hooks/theme.hook'
import { useAuthContext } from '../../../../contexts/auth-context'
import { MemberActionsModal } from './member-actions.modal'
import { MelddSearchBar } from '../../../../@ui/form/meldd-search.input'

export type NodeMembersScreenNode = NonNullable<NodeByIdQuery['nodeById']>

interface NodeMemberListProps {
  node: NodeMembersScreenNode
  onUpdate: () => void
  onSwitchMode: () => void
}

export interface NodeMemberListRef {
  refetch: () => void
}

const RoleDropdown = ({
  member,
  onUpdate,
}: {
  member: NodeMember
  onUpdate: () => void
}) => {
  const { userId: ownerId } = useAuthContext()
  const { t } = useTranslation('nodes')
  const theme = useTheme()
  const [doUpdateMember] = useUpdateNodeMemberMutation()
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  async function changeRole(id: string, isAdmin: boolean) {
    await doUpdateMember({
      variables: {
        input: { id, isAdmin },
      },
    })

    onUpdate()
  }

  const isOwner = member.userId === ownerId
  if (isOwner) {
    return (
      <Chip
        mode="flat"
        style={{
          backgroundColor: theme.colors.primaryContainer,
        }}
      >
        {t('owner')}
      </Chip>
    )
  }
  return (
    <>
      <Menu
        visible={openMenuId === member.id}
        onDismiss={() => setOpenMenuId(null)}
        anchor={
          <Chip
            mode="flat"
            onPress={() => setOpenMenuId(member.id)}
            style={{
              backgroundColor: member.isAdmin
                ? theme.colors.tertiaryContainer
                : theme.colors.surfaceVariant,
            }}
          >
            {member.isAdmin ? t('admin') : t('member')}
          </Chip>
        }
      >
        <Menu.Item
          onPress={() => {
            changeRole(member.id, true)
            setOpenMenuId(null)
          }}
          title={t('admin')}
          leadingIcon={member.isAdmin ? 'check' : undefined}
        />
        <Menu.Item
          onPress={() => {
            changeRole(member.id, false)
            setOpenMenuId(null)
          }}
          title={t('member')}
          leadingIcon={!member.isAdmin ? 'check' : undefined}
        />
      </Menu>
    </>
  )
}
export const NodeMemberList = forwardRef<
  NodeMemberListRef,
  NodeMemberListProps
>(({ node, onUpdate, onSwitchMode }, ref) => {
  const { t } = useTranslation('nodes')
  const theme = useTheme()
  const navigation = useNavigation()
  const [modalVisible, setModalVisible] = useState(false)
  const [selectedMember, setSelectedMember] = useState<any>(null)
  const { userId: ownerId } = useAuthContext()
  const [search, setSearch] = useState<string>('')

  const { data, refetch } = useNodeMembersByNodeIdQuery({
    variables: {
      input: {
        nodeId: node.id,
        search,
        offset: 0,
        limit: 10,
      },
    },
  })

  const isFocused = useIsFocused()

  React.useEffect(() => {
    if (isFocused) {
      refetch()
    }
  }, [isFocused, refetch])

  React.useEffect(() => {
    refetch({
      input: {
        nodeId: node.id,
        search,
        offset: 0,
      },
    })
  }, [search, refetch, node.id])

  useImperativeHandle(
    ref,
    () => ({
      refetch: () => refetch(),
    }),
    [refetch]
  )

  async function handleActionSuccess() {
    await refetch()
    onUpdate()
    setModalVisible(false)
  }

  function handleTransferSuccess() {
    setModalVisible(false)
    navigation.navigate('MyNodes' as never)
  }

  function showActionsModal(member: any) {
    setSelectedMember(member)
    setModalVisible(true)
  }

  const styles = useMemo(
    () =>
      StyleSheet.create({
        searchContainer: {
          marginBottom: theme.spacing(2),
          flexDirection: 'row',
          gap: theme.spacing(2),
          alignItems: 'center',
        },
      }),
    [theme]
  )

  return (
    <View>
      <View style={styles.searchContainer}>
        <View style={{ flex: 1 }}>
          <MelddSearchBar
            value={search}
            onChangeText={setSearch}
            placeholder={t('filter-members')}
          />
        </View>
        <Button mode="outlined" icon="account-plus" onPress={onSwitchMode}>
          {t('add')}
        </Button>
      </View>
      <PaginatedFlatList
        data={(data?.nodeMembersByNodeId.results || []) as any[]}
        emptyMessage={search ? t('no-members-match-search') : undefined}
        emptySubMessage={
          search ? undefined : t('you-can-add-new-members-with-add-button')
        }
        renderItem={({ item }: any) => {
          const isOwner = ownerId === (item as NodeMember).userId

          const member = item as NodeMember
          return (
            <View>
              <List.Item
                title={member.user.username}
                left={() => (
                  <Avatar.Text size={32} label={member.user.username[0]} />
                )}
                right={() => (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <RoleDropdown
                      member={member}
                      onUpdate={handleActionSuccess}
                    />
                    {!isOwner && (node.isOwner || !member.isAdmin) && (
                      <IconButton
                        icon="dots-vertical"
                        onPress={() => showActionsModal(member)}
                        accessibilityLabel={t('actions')}
                      />
                    )}
                  </View>
                )}
              />
            </View>
          )
        }}
        onLoadMore={async (params) => {
          await refetch({
            input: {
              nodeId: node.id,
              search,
              ...params,
            },
          })
        }}
        total={data?.nodeMembersByNodeId.total || 0}
        onRefresh={async () => {
          await refetch({
            input: { nodeId: node.id, search, offset: 0 },
          })
        }}
      />
      {selectedMember && (
        <MemberActionsModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          node={node}
          member={selectedMember}
          onTransferSuccess={handleTransferSuccess}
          onDeleteSuccess={handleActionSuccess}
        />
      )}
    </View>
  )
})

const styles = StyleSheet.create({})
